/**
 * Badge d'état licence par entreprise (liste portefeuille / tableau cabinet).
 * Lit uniquement le JSON stocké via SecureLicenceStore.getLicenceStatus — pas de logique métier.
 * Kinds alignés sur LicenceRestriction / parseStored (LicenceStatusService).
 */
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { EXPIRED_MESSAGE } from '@infra/licence/LicenceStatusService';
import { esc } from '@presentation/shell/format';

/** Kinds persistés ou runtime (mêmes valeurs que LicenceRestriction['kind']). */
export type PortfolioLicenceKind =
  | 'revoked'
  | 'suspended'
  | 'offline_too_long'
  | 'expired'
  | 'active'
  | 'none';

export interface PortfolioLicenceBadgeView {
  kind: PortfolioLicenceKind;
  label: string;
  /** Classe sémantique existante : texte + couleur (WCAG). */
  className: string;
}

const KIND_LABELS: Record<PortfolioLicenceKind, string> = {
  revoked: 'Révoquée',
  suspended: 'Suspendue',
  offline_too_long: 'Absence de contrôle',
  expired: 'Expirée',
  active: 'OK',
  none: '—',
};

/**
 * Parse défensif du JSON `{ kind, since, message? }` — mêmes kinds que parseStored,
 * plus détection UI de l'expiration (message EXPIRED_MESSAGE, kind stocké `suspended`).
 */
export function parsePortfolioLicenceStatus(
  raw: string | null,
  fallbackLicensed?: boolean,
): PortfolioLicenceBadgeView {
  if (raw) {
    try {
      const v = JSON.parse(raw) as { kind?: unknown; since?: unknown; message?: unknown };
      const kind = typeof v.kind === 'string' ? v.kind : '';
      const message = typeof v.message === 'string' ? v.message : '';

      if (kind === 'revoked' && typeof v.since === 'string') {
        return viewOf('revoked');
      }
      if (kind === 'offline_too_long') {
        return viewOf('offline_too_long');
      }
      if (kind === 'suspended' && typeof v.since === 'string') {
        // license_expired est persisté en kind=suspended + EXPIRED_MESSAGE
        if (message === EXPIRED_MESSAGE || /expir/i.test(message)) {
          return viewOf('expired');
        }
        return viewOf('suspended');
      }
      // Kind inconnu / JSON partiel : ne pas inventer d'état restrictif
    } catch {
      /* JSON illisible → repli fallback */
    }
  }

  if (fallbackLicensed === true) return viewOf('active');
  if (fallbackLicensed === false) return viewOf('none');
  return viewOf('none');
}

function viewOf(kind: PortfolioLicenceKind): PortfolioLicenceBadgeView {
  const label = KIND_LABELS[kind];
  const className =
    kind === 'active'
      ? 'pf-badge pf-ok'
      : kind === 'none'
        ? 'pf-badge'
        : 'pf-badge pf-alert';
  return { kind, label, className };
}

/** HTML badge : libellé FR + classes (pas de couleur seule). Contenu échappé. */
export function portfolioLicenceBadgeHtml(view: PortfolioLicenceBadgeView): string {
  const title =
    view.kind === 'active'
      ? 'Licence active'
      : view.kind === 'none'
        ? 'Aucune licence'
        : `Licence ${view.label.toLowerCase()}`;
  return `<span class="${view.className}" role="status" title="${esc(title)}">${esc(view.label)}</span>`;
}

/**
 * Badge HTML pour une entreprise du portefeuille.
 * @param tenantId id dossier / tenant
 * @param fallbackLicensed résumé portefeuille (`licensed`) si aucun statut stocké
 */
export async function renderPortfolioLicenceBadgeHtml(
  tenantId: string,
  fallbackLicensed?: boolean,
): Promise<string> {
  let raw: string | null = null;
  try {
    raw = await SecureLicenceStore.getLicenceStatus(tenantId);
  } catch {
    // Coffre illisible : repli sur le résumé portefeuille, sans casser le rendu des autres entreprises.
    raw = null;
  }
  const view = parsePortfolioLicenceStatus(raw, fallbackLicensed);
  return portfolioLicenceBadgeHtml(view);
}
